import { SkeletonWithoutBreadcrumbs } from '@townsquare/griffin-main-header/skeleton';
import Loadable from '@townsquare/loadable';
import { GlobalHomePageRoute, SiteHomePageRoute } from '@townsquare/router/route-types';
import { goalsAppUpdateViewRoute, goalUpdateViewRoute } from '@townsquare/routes/goal-updates-view';
import {
	projectsAppUpdateViewRoute,
	projectUpdateViewRoute,
} from '@townsquare/routes/project-updates-view';
import { recentsViewRoute } from '@townsquare/routes/recents-view';
import {
	savedViewsUpdatesRoute,
	goalsAppSavedViewsUpdatesRoute,
	projectsAppSavedViewsUpdatesRoute,
} from '@townsquare/routes/saved-views-updates-view';
import { tagFeedViewRoute } from '@townsquare/routes/tag-feed-view';
import { teamUpdatesViewRoute } from '@townsquare/routes/team-updates-view';

import { goalUpdatesAsideAggResource } from './resources/goalUpdatesAsideAggResource';
import { goalUpdatesAsideResource } from './resources/goalUpdatesAsideResource';
import { goalUpdatesResource } from './resources/goalUpdatesResource';
import { goalUpdatesViewAggResource } from './resources/goalUpdatesViewAggResource';
import { projectUpdatesAsideResource } from './resources/projectUpdatesAsideResource';
import { projectUpdatesResource } from './resources/projectUpdatesResource';
import { savedViewUpdatesAggResource } from './resources/savedViewUpdatesAggResource';
import { savedViewUpdatesAsideAggResource } from './resources/savedViewUpdatesAsideAggResource';
import { savedViewUpdatesAsideResource } from './resources/savedViewUpdatesAsideResource';
import { savedViewUpdatesResource } from './resources/savedViewUpdatesResource';
import { statusUpdatesNavigationAggResource } from './resources/statusUpdatesNavigationAggResource';
import { statusUpdatesNavigationResource } from './resources/statusUpdatesNavigationResource';
import { stayInLoopResource } from './resources/stayInLoopResource';
import { tagUpdatesAggResource } from './resources/tagUpdatesAggResource';
import { tagUpdatesAsideResource } from './resources/tagUpdatesAsideResource';
import { tagUpdatesResource } from './resources/tagUpdatesResource';
import { teamUpdatesAggResource } from './resources/teamUpdatesAggResource';
import { teamUpdatesAsideResource } from './resources/teamUpdatesAsideResource';
import { teamUpdatesResource } from './resources/teamUpdatesResource';

const headerLoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "statusUpdatesHeader-view" */ './StatusUpdatesHeader/StatusUpdatesHeader'
		),
	loading: SkeletonWithoutBreadcrumbs,
});

const headerLoadableComponentAgg = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "statusUpdatesHeader-agg-view" */ './StatusUpdatesHeader/agg/StatusUpdatesHeaderAgg'
		),
	loading: SkeletonWithoutBreadcrumbs,
});

// Goal Updates View
const GoalUpdatesViewLoadable = Loadable({
	loader: () => import(/* webpackChunkName: "goalUpdate-view" */ './GoalUpdatesView/index'),
	loading: () => null,
});
const GoalUpdatesAsideViewLoadable = Loadable({
	loader: () =>
		import(/* webpackChunkName: "goalUpdateAside-view" */ './GoalUpdatesView/GoalUpdatesAsideView'),
	loading: () => null,
});
const GoalUpdatesViewAggLoadable = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "goalUpdate-agg-view" */ './GoalUpdatesView/agg/GoalUpdatesViewAgg'
		).then((module) => ({
			default: module.GoalUpdatesViewAgg,
		})),
	loading: () => null,
});
const GoalUpdatesAsideViewAggLoadable = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "goalUpdateAside-agg-view" */ './GoalUpdatesView/agg/GoalUpdatesAsideViewAgg'
		),
	loading: () => null,
});

goalUpdateViewRoute.component = GoalUpdatesViewLoadable;
goalUpdateViewRoute.aside = GoalUpdatesAsideViewLoadable;
goalUpdateViewRoute.header = headerLoadableComponent;
goalUpdateViewRoute.resources = [
	goalUpdatesResource,
	goalUpdatesAsideResource,
	stayInLoopResource,
	statusUpdatesNavigationResource,
];

goalUpdateViewRoute.featureGating = {
	gates: ['goal_types_m3_bundled_updates'],
	behavior: {
		type: 'override',
		override: {
			resources: [
				goalUpdatesResource,
				goalUpdatesAsideResource,
				stayInLoopResource,
				statusUpdatesNavigationResource,
			],
		},
	},
};

export const goalUpdateViewRouterRoute: GlobalHomePageRoute = goalUpdateViewRoute;

goalsAppUpdateViewRoute.component = GoalUpdatesViewLoadable;
goalsAppUpdateViewRoute.aside = GoalUpdatesAsideViewLoadable;
goalsAppUpdateViewRoute.header = headerLoadableComponent;
goalsAppUpdateViewRoute.resources = [
	goalUpdatesResource,
	goalUpdatesAsideResource,
	statusUpdatesNavigationResource,
];

goalsAppUpdateViewRoute.featureGating = {
	gates: ['goal_types_m3_bundled_updates'],
	behavior: {
		type: 'override',
		override: {
			component: GoalUpdatesViewAggLoadable,
			aside: GoalUpdatesAsideViewAggLoadable,
			header: headerLoadableComponentAgg,
			relayEnvironment: 'agg',
			resources: [
				goalUpdatesViewAggResource,
				goalUpdatesAsideAggResource,
				statusUpdatesNavigationAggResource,
			],
		},
	},
};

export const goalsAppUpdateViewRouterRoute: GlobalHomePageRoute = goalsAppUpdateViewRoute;

// Project Updates View
const ProjectUpdatesViewLoadable = Loadable({
	loader: () =>
		import(/* webpackChunkName: "projectUpdate-view" */ './ProjectUpdatesView/ProjectUpdatesView'),
	loading: () => null,
});
const ProjectUpdatesAsideViewLoadable = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "projectUpdateAside-view" */ './ProjectUpdatesView/ProjectUpdatesAsideView'
		),
	loading: () => null,
});

projectUpdateViewRoute.component = ProjectUpdatesViewLoadable;
projectUpdateViewRoute.aside = ProjectUpdatesAsideViewLoadable;
projectUpdateViewRoute.header = headerLoadableComponent;
projectUpdateViewRoute.resources = [
	projectUpdatesResource,
	projectUpdatesAsideResource,
	stayInLoopResource,
	statusUpdatesNavigationResource,
];
export const projectUpdateViewRouterRoute: GlobalHomePageRoute = projectUpdateViewRoute;

projectsAppUpdateViewRoute.component = ProjectUpdatesViewLoadable;
projectsAppUpdateViewRoute.aside = ProjectUpdatesAsideViewLoadable;
projectsAppUpdateViewRoute.header = headerLoadableComponent;
projectsAppUpdateViewRoute.resources = [
	projectUpdatesResource,
	projectUpdatesAsideResource,
	stayInLoopResource,
	statusUpdatesNavigationResource,
];
export const projectsAppUpdateViewRouterRoute: GlobalHomePageRoute = projectsAppUpdateViewRoute;

// Saved Views Updates
const SavedViewsUpdatesLoadable = Loadable({
	loader: () => import(/* webpackChunkName: "savedViewsUpdates-view" */ './SavedViewUpdates/index'),
	loading: () => null,
});

const SavedViewsUpdatesAsideLoadable = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "savedViewsUpdates-aside-view" */ './SavedViewUpdates/SavedViewUpdatesAside'
		),
	loading: () => null,
});

const SavedViewsUpdatesAggLoadable = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "savedViewsUpdates-agg-view" */ './SavedViewUpdates/agg/SavedViewUpdatesAgg'
		).then((module) => ({ default: module.SavedViewUpdatesAgg })),
	loading: () => null,
});

const SavedViewsUpdatesAsideAggLoadable = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "savedViewsUpdates-agg-aside-view" */ './SavedViewUpdates/agg/SavedViewUpdatesAsideAgg'
		),
	loading: () => null,
});

savedViewsUpdatesRoute.component = SavedViewsUpdatesLoadable;
savedViewsUpdatesRoute.aside = SavedViewsUpdatesAsideLoadable;
savedViewsUpdatesRoute.header = headerLoadableComponent;
savedViewsUpdatesRoute.resources = [
	savedViewUpdatesResource,
	savedViewUpdatesAsideResource,
	statusUpdatesNavigationResource,
];
export const savedViewsUpdatesRouterRoute: SiteHomePageRoute = savedViewsUpdatesRoute;

goalsAppSavedViewsUpdatesRoute.component = SavedViewsUpdatesLoadable;
goalsAppSavedViewsUpdatesRoute.aside = SavedViewsUpdatesAsideLoadable;
goalsAppSavedViewsUpdatesRoute.header = headerLoadableComponent;
goalsAppSavedViewsUpdatesRoute.resources = [
	savedViewUpdatesResource,
	savedViewUpdatesAsideResource,
	statusUpdatesNavigationResource,
];
goalsAppSavedViewsUpdatesRoute.featureGating = {
	gates: ['goal_types_m3_bundled_updates'],
	behavior: {
		type: 'override',
		override: {
			component: SavedViewsUpdatesAggLoadable,
			aside: SavedViewsUpdatesAsideAggLoadable,
			header: headerLoadableComponentAgg,
			relayEnvironment: 'agg',
			resources: [
				savedViewUpdatesAggResource,
				savedViewUpdatesAsideAggResource,
				statusUpdatesNavigationAggResource,
			],
		},
	},
};
export const goalsAppSavedViewsUpdatesRouterRoute: SiteHomePageRoute =
	goalsAppSavedViewsUpdatesRoute;

projectsAppSavedViewsUpdatesRoute.component = SavedViewsUpdatesLoadable;
projectsAppSavedViewsUpdatesRoute.aside = SavedViewsUpdatesAsideLoadable;
projectsAppSavedViewsUpdatesRoute.header = headerLoadableComponent;
projectsAppSavedViewsUpdatesRoute.resources = [
	savedViewUpdatesResource,
	savedViewUpdatesAsideResource,
	statusUpdatesNavigationResource,
];
export const projectsAppSavedViewsUpdatesRouterRoute: SiteHomePageRoute =
	projectsAppSavedViewsUpdatesRoute;

// Tag Feed View
const TagFeedViewLoadable = Loadable({
	loader: () => import(/* webpackChunkName: "TagUpdatesView" */ './TagUpdatesView'),
	loading: () => null,
});
const TagFeedAsideViewLoadable = Loadable({
	loader: () =>
		import(/* webpackChunkName: "TagUpdatesAsideView" */ './TagUpdatesView/TagUpdatesAsideView'),
	loading: () => null,
});

tagFeedViewRoute.component = TagFeedViewLoadable;
tagFeedViewRoute.aside = TagFeedAsideViewLoadable;
tagFeedViewRoute.header = headerLoadableComponent;
tagFeedViewRoute.resources = [
	tagUpdatesResource,
	tagUpdatesAsideResource,
	statusUpdatesNavigationResource,
];
tagFeedViewRoute.featureGating = {
	gates: ['goal_types_m3_bundled_updates'],
	behavior: {
		type: 'override',
		override: {
			resources: [
				tagUpdatesResource,
				tagUpdatesAggResource,
				tagUpdatesAsideResource,
				statusUpdatesNavigationResource,
			],
		},
	},
};
export const tagFeedViewRouterRoute: SiteHomePageRoute = tagFeedViewRoute;

// Recents View
const RecentsViewLoadable = Loadable({
	loader: () => import(/* webpackChunkName: "RecentsView" */ './RecentsView'),
	loading: () => null,
});
const RecentsViewHeaderLoadable = Loadable({
	loader: () => import(/* webpackChunkName: "recents-view-header" */ './RecentsViewHeader'),
	loading: SkeletonWithoutBreadcrumbs,
});

recentsViewRoute.component = RecentsViewLoadable;
recentsViewRoute.header = RecentsViewHeaderLoadable;
export const recentsViewRouterRoute: GlobalHomePageRoute = recentsViewRoute;

// Team Updates View
const TeamUpdatesViewLoadable = Loadable({
	loader: () => import(/* webpackChunkName: "teamUpdates-view" */ './TeamUpdatesView/index'),
	loading: () => null,
});
const TeamUpdatesAsideViewLoadable = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "teamUpdates-aside-view" */ './TeamUpdatesView/TeamUpdatesAsideView'
		),
	loading: () => null,
});

teamUpdatesViewRoute.component = TeamUpdatesViewLoadable;
teamUpdatesViewRoute.aside = TeamUpdatesAsideViewLoadable;
teamUpdatesViewRoute.header = headerLoadableComponent;
teamUpdatesViewRoute.resources = [
	teamUpdatesResource,
	teamUpdatesAsideResource,
	statusUpdatesNavigationResource,
];
teamUpdatesViewRoute.featureGating = {
	gates: ['goal_types_m3_bundled_updates'],
	behavior: {
		type: 'override',
		override: {
			resources: [
				teamUpdatesResource,
				teamUpdatesAggResource,
				teamUpdatesAsideResource,
				statusUpdatesNavigationResource,
			],
		},
	},
};
export const teamUpdatesViewRouterRoute: GlobalHomePageRoute = teamUpdatesViewRoute;
